import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  useTheme,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Skeleton,
  Pagination,
  Button,
  IconButton,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search,
  Inventory,
  MiscellaneousServices,
  Business as BusinessIcon,
  Star,
  PictureAsPdf,
  ShareOutlined,
  FilterList,
  ExpandMore,
  ExpandLess,
  MoreVert,
  Flag,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ServiceProduct, ServiceType } from '@/constants/types';
import { serviceProductService } from '@/services/business/serviceProductService';
import { getFileUrl } from '@/utils/businessHelpers';
import { ShareProductModal } from '@/components/business/ShareProductModal';
import { ReportServiceProductDialog } from '@/components/business/ReportServiceProductDialog';

const CardSkeleton: React.FC = () => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
    <Skeleton variant="rectangular" height={180} />
    <CardContent>
      <Skeleton width="30%" height={20} sx={{ mb: 1 }} />
      <Skeleton width="80%" height={24} sx={{ mb: 0.5 }} />
      <Skeleton width="40%" height={28} sx={{ mb: 0.5 }} />
      <Skeleton width="100%" height={20} />
    </CardContent>
  </Card>
);

export const ExploreProductsServicesPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [products, setProducts] = useState<ServiceProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState<ServiceType | 'all'>('all');
  const [promotedOnly, setPromotedOnly] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 100000]);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0,
    limit: 12,
  });
  const [shareProduct, setShareProduct] = useState<ServiceProduct | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement | null; product: ServiceProduct | null }>({ el: null, product: null });
  const [reportProduct, setReportProduct] = useState<ServiceProduct | null>(null);

  useEffect(() => {
    loadProducts();
  }, [searchQuery, serviceTypeFilter, promotedOnly, priceRange, page]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, serviceTypeFilter, promotedOnly, priceRange]);

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      const params: any = {
        page,
        limit: pageSize,
        isActive: true,
      };

      if (searchQuery) params.search = searchQuery;
      if (serviceTypeFilter !== 'all') params.serviceType = serviceTypeFilter;
      if (promotedOnly) params.promoted = true;
      if (priceRange[0] > 0) params.minPrice = priceRange[0];
      if (priceRange[1] < 100000) params.maxPrice = priceRange[1];

      const response = await serviceProductService.getServiceProducts(params);
      setProducts(response.data.serviceProducts);
      setPagination(response.data.pagination);
    } catch (err: any) {
      console.error('Failed to load products:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewProduct = (product: ServiceProduct) => {
    navigate(`/products-services/${product._id}`);
  };

  const getBusinessInfo = (product: ServiceProduct) => {
    if (typeof product.businessId === 'object' && product.businessId) {
      return product.businessId;
    }
    return null;
  };

  const totalPages = pagination ? Math.ceil(pagination.total / pageSize) : 1;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        Explore Products & Services
      </Typography>

      {/* Search & Filters */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid size={{ xs: 12, md: 10 }}>
            <TextField
              fullWidth
              placeholder="Search products, services, or businesses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
              size="small"
            />
          </Grid>

          {/* More Filters Toggle */}
          <Grid size={{ xs: 12, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterList />}
              endIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ textTransform: 'none' }}
            >
              Filters
            </Button>
          </Grid>
        </Grid>

        {/* Advanced Filters */}
        <Collapse in={showFilters}>
          <Box sx={{ mt: 2, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
            <Grid container spacing={3} alignItems="flex-end">
              {/* Type Filter */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Type
                </Typography>
                <ToggleButtonGroup
                  value={serviceTypeFilter}
                  exclusive
                  onChange={(_, value) => value && setServiceTypeFilter(value)}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="all" sx={{ textTransform: 'none' }}>
                    All
                  </ToggleButton>
                  <ToggleButton value="product" sx={{ textTransform: 'none' }}>
                    <Inventory sx={{ mr: 0.5, fontSize: 18 }} /> Products
                  </ToggleButton>
                  <ToggleButton value="service" sx={{ textTransform: 'none' }}>
                    <MiscellaneousServices sx={{ mr: 0.5, fontSize: 18 }} /> Services
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              {/* Promoted Only */}
              {/* <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Featured
                </Typography>
                <ToggleButtonGroup
                  value={promotedOnly ? 'promoted' : 'all'}
                  exclusive
                  onChange={(_, value) => setPromotedOnly(value === 'promoted')}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="all" sx={{ textTransform: 'none' }}>
                    All
                  </ToggleButton>
                  <ToggleButton value="promoted" sx={{ textTransform: 'none' }}>
                    <Star sx={{ mr: 0.5, fontSize: 16 }} /> Promoted
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid> */}

              {/* Price Range */}
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Price Range: ₹{priceRange[0].toLocaleString()} - ₹{priceRange[1].toLocaleString()}
                </Typography>
                <Slider
                  value={priceRange}
                  onChange={(_, value) => setPriceRange(value as [number, number])}
                  valueLabelDisplay="auto"
                  min={0}
                  max={100000}
                  step={500}
                  valueLabelFormat={(value) => `₹${value.toLocaleString()}`}
                />
              </Grid>

              {/* Clear Filters */}
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Button
                  variant="text"
                  onClick={() => {
                    setSearchQuery('');
                    setServiceTypeFilter('all');
                    setPromotedOnly(false);
                    setPriceRange([0, 100000]);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Box>

      {/* Results Count */}
      {!isLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {pagination.total} {pagination.total === 1 ? 'result' : 'results'} found
        </Typography>
      )}

      {/* Product Cards */}
      {isLoading ? (
        <Grid container spacing={2}>
          {Array.from({ length: pageSize }).map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
              <CardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : products.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <Inventory sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No products or services found
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Try adjusting your filters
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {products.map((product) => {
            const businessInfo = getBusinessInfo(product);
            const bizId = typeof product.businessId === 'string'
              ? product.businessId
              : product.businessId?._id;

            return (
              <Grid key={product._id} size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                <Card
                  elevation={0}
                  onClick={() => handleViewProduct(product)}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      boxShadow: 2,
                    },
                  }}
                >
                  {/* Promoted Badge */}
                  {product.isPromoted && (
                    <Chip
                      icon={<Star sx={{ fontSize: 14 }} />}
                      label="Promoted"
                      size="small"
                      color="warning"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        zIndex: 1,
                        height: 22,
                        fontSize: '0.7rem',
                      }}
                    />
                  )}

                  {/* Kebab menu - top right overlay */}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuAnchor({ el: e.currentTarget, product });
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      bgcolor: 'rgba(255,255,255,0.9)',
                      color: 'text.primary',
                      boxShadow: 1,
                      '&:hover': { bgcolor: 'white' },
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>

                  {/* Product Image */}
                  {product.productImage?.url ? (
                    <CardMedia
                      component="img"
                      height={160}
                      image={product.productImage.url}
                      alt={product.title}
                      sx={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 160,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                      }}
                    >
                      {product.serviceType === 'product' ? (
                        <Inventory sx={{ fontSize: 48, color: 'text.disabled' }} />
                      ) : (
                        <MiscellaneousServices sx={{ fontSize: 48, color: 'text.disabled' }} />
                      )}
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Type Chip */}
                    <Chip
                      label={product.serviceType === 'product' ? 'Product' : 'Service'}
                      size="small"
                      color={product.serviceType === 'product' ? 'primary' : 'secondary'}
                      variant="outlined"
                      sx={{ mb: 1, height: 22, fontSize: '0.7rem' }}
                    />

                    {/* Title */}
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {product.title}
                    </Typography>

                    {/* Price */}
                    <Typography variant="h6" color="primary" fontWeight={700} sx={{ my: 0.5 }}>
                      {product.price !== undefined && product.price > 0
                        ? `₹${product.price.toLocaleString('en-IN')}`
                        : 'Price on Request'}
                    </Typography>

                    {/* Description */}
                    {product.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mb: 1,
                        }}
                      >
                        {product.description}
                      </Typography>
                    )}

                    {/* Business Info */}
                    {businessInfo && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mt: 1,
                          pt: 1,
                          borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (bizId) navigate(`/businesses/${bizId}`);
                        }}
                      >
                        <Avatar
                          src={businessInfo.logoId ? getFileUrl(businessInfo.logoId) : undefined}
                          sx={{ width: 24, height: 24, fontSize: 12 }}
                        >
                          {businessInfo.businessName?.charAt(0) || <BusinessIcon sx={{ fontSize: 14 }} />}
                        </Avatar>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                          {businessInfo.businessName}
                        </Typography>
                      </Box>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                      {product.brochure?.url && (
                        <Button
                          size="small"
                          startIcon={<PictureAsPdf />}
                          href={product.brochure.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ textTransform: 'none' }}
                        >
                          Brochure
                        </Button>
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShareProduct(product);
                        }}
                        sx={{ ml: 'auto', border: '1px solid', borderColor: 'divider' }}
                      >
                        <ShareOutlined fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      {/* Share Product Modal */}
      <ShareProductModal
        open={!!shareProduct}
        onClose={() => setShareProduct(null)}
        product={shareProduct}
        businessId={
          shareProduct
            ? typeof shareProduct.businessId === 'string'
              ? shareProduct.businessId
              : shareProduct.businessId?._id || ''
            : ''
        }
      />

      {/* Card Kebab Menu */}
      <Menu
        anchorEl={menuAnchor.el}
        open={Boolean(menuAnchor.el)}
        onClose={() => setMenuAnchor({ el: null, product: null })}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            const p = menuAnchor.product;
            setMenuAnchor({ el: null, product: null });
            if (p) setReportProduct(p);
          }}
        >
          <ListItemIcon>
            <Flag fontSize="small" />
          </ListItemIcon>
          <ListItemText>Report</ListItemText>
        </MenuItem>
      </Menu>

      {/* Report Dialog */}
      <ReportServiceProductDialog
        open={!!reportProduct}
        onClose={() => setReportProduct(null)}
        productId={reportProduct?._id}
        productTitle={reportProduct?.title}
      />
    </Box>
  );
};
